import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';

import { fNumber } from 'src/utils/format-number';

import { useAppDispatcher } from 'src/redux/store';
import { IListUser } from 'src/redux/slices/users';
import { updateReorderSettingsRow } from 'src/redux/slices/reorder-settings';

import { IReorderSettingsRow } from 'src/types/reorder-settings';

type Props = {
  row: IReorderSettingsRow;
  allUsers: IListUser[];
};

const getUserLabel = (user: { firstName: string; lastName?: string }) =>
  `${user.firstName} ${user.lastName ?? ''}`.trim();

export default function ReorderSettingsTableRow({ row, allUsers }: Props) {
  const dispatch = useAppDispatcher();

  const { _id, itemType, itemCode, itemName, quantity, reorderThreshold, unit } = row;

  // Local text state so the field can be freely typed into (including
  // transiently empty/invalid) without committing on every keystroke —
  // only on blur, mirroring a spreadsheet cell.
  const [newOrderQtyInput, setNewOrderQtyInput] = useState(
    row.newOrderQty !== null ? String(row.newOrderQty) : ''
  );

  useEffect(() => {
    setNewOrderQtyInput(row.newOrderQty !== null ? String(row.newOrderQty) : '');
  }, [row.newOrderQty]);

  const unitLabel = unit?.abbreviation ?? '';

  const handleNewOrderQtyBlur = async () => {
    const trimmed = newOrderQtyInput.trim();
    const previousValue = row.newOrderQty;

    if (trimmed === (previousValue !== null ? String(previousValue) : '')) {
      // Unchanged — nothing to save.
      return;
    }

    const parsed = Number(trimmed);
    if (trimmed === '' || Number.isNaN(parsed) || parsed <= 0) {
      enqueueSnackbar('New Order Qty must be a positive number greater than 0', {
        variant: 'error',
      });
      setNewOrderQtyInput(previousValue !== null ? String(previousValue) : '');
      return;
    }

    try {
      await dispatch(
        updateReorderSettingsRow({ itemType, id: _id, newOrderQty: parsed })
      ).unwrap();

      enqueueSnackbar(
        `${itemName}: New Order Qty changed from ${
          previousValue ?? '—'
        } to ${parsed}${unitLabel ? ` ${unitLabel}` : ''}`
      );
    } catch (e) {
      setNewOrderQtyInput(previousValue !== null ? String(previousValue) : '');
      enqueueSnackbar(typeof e === 'string' ? e : e?.message ?? 'Update failed', {
        variant: 'error',
      });
    }
  };

  const handleOrderByChange = async (newUser: IListUser | null) => {
    if (!newUser || newUser._id === row.orderBy?._id) {
      return;
    }

    const previousLabel = row.orderBy ? getUserLabel(row.orderBy) : '—';

    try {
      await dispatch(
        updateReorderSettingsRow({ itemType, id: _id, orderBy: newUser._id })
      ).unwrap();

      enqueueSnackbar(
        `${itemName}: Order By changed from ${previousLabel} to ${getUserLabel(newUser)}`
      );
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message ?? 'Update failed', {
        variant: 'error',
      });
    }
  };

  const handleHiddenFromMyTasksChange = async (checked: boolean) => {
    try {
      await dispatch(
        updateReorderSettingsRow({ itemType, id: _id, hiddenFromMyTasks: checked })
      ).unwrap();

      enqueueSnackbar(
        checked
          ? `${itemName}: Occasional Item enabled — hidden from My Tasks until it's back in stock`
          : `${itemName}: Occasional Item disabled — will show in My Tasks when low/out of stock`
      );
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message ?? 'Update failed', {
        variant: 'error',
      });
    }
  };

  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>{itemCode}</TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>{itemName}</TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        {fNumber(quantity)} {unitLabel}
      </TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        {fNumber(reorderThreshold)} {unitLabel}
      </TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 140 }}>
        <TextField
          size="small"
          type="number"
          value={newOrderQtyInput}
          onChange={(e) => setNewOrderQtyInput(e.target.value)}
          onBlur={handleNewOrderQtyBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder="Not set"
          InputProps={{
            endAdornment: unitLabel ? (
              <InputAdornment position="end">{unitLabel}</InputAdornment>
            ) : undefined,
            inputProps: { min: 0, style: { textAlign: 'right' } },
          }}
          sx={{ width: 1 }}
        />
      </TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>
        <Autocomplete
          size="small"
          options={allUsers}
          value={row.orderBy ?? null}
          onChange={(_e, newValue) => handleOrderByChange(newValue as IListUser | null)}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          getOptionLabel={(option) => getUserLabel(option)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Not assigned" />
          )}
          sx={{ width: 1 }}
        />
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
        <Tooltip title="Ordered only occasionally — hides this item from My Tasks while it's low or out of stock. Turns itself off once it's back in stock.">
          <Switch
            checked={row.hiddenFromMyTasks}
            onChange={(_e, checked) => handleHiddenFromMyTasksChange(checked)}
          />
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
